import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { Badge } from '../../../components/ui/badge';
import { formatNoteHTML, getNoteTypeBadgeInfo, getBadgeInfoFromNoteName } from '../../../utils/noteTypeMapper';

/**
 * MedicalNotesDisplay - Displays medical coverage notes for a specific indication
 */
const MedicalNotesDisplay = ({ notes, indicationName }) => {
  if (!notes || notes.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground text-center">
            Medical coverage notes not provided by plan for this indication
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Medical Coverage Notes - {indicationName}</CardTitle>
        <CardDescription className="text-xs">
          Medical policy, prior authorization, step therapy, and specialty pharmacy notes
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" className="w-full">
          {notes.map((note, index) => {
            const formattedNote = formatNoteHTML(note.Note);
            
            // CRITICAL: Medical notes have different NoteTypeId values than formulary notes
            // Always use name-based lookup for medical notes since they have note.NoteType.name
            let badgeInfo;
            
            if (note.NoteType?.name) {
              // Use name-based lookup for medical notes
              badgeInfo = getBadgeInfoFromNoteName(note.NoteType.name);
            } else {
              // Fallback to ID-based lookup for formulary notes
              badgeInfo = getNoteTypeBadgeInfo(note.NoteTypeId);
            }

            return (
              <AccordionItem key={index} value={`note-${index}`}>
                <AccordionTrigger className="hover:no-underline text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant={badgeInfo.variant} className="text-xs">{badgeInfo.text}</Badge>
                    <span className="font-medium">{note.NoteType?.name || 'Note'}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="bg-muted rounded-lg p-3 mt-2">
                    <pre className="whitespace-pre-wrap text-xs font-sans text-foreground">
                      {formattedNote}
                    </pre>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default MedicalNotesDisplay;
